import React, { useEffect, useState } from 'react';
import { 
  XMarkIcon,
  DocumentDuplicateIcon,
  CheckIcon,
  InformationCircleIcon,
  ShareIcon
} from '@heroicons/react/24/outline';

interface ShareInviteLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareLink: string | null;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

const ShareInviteLinkModal: React.FC<ShareInviteLinkModalProps> = ({
  isOpen,
  onClose,
  shareLink,
  loading = false,
  error = null,
  onRetry,
}) => {
  const [copied, setCopied] = useState(false);
  const inviteLink = shareLink ?? '';

  useEffect(() => {
    if (!isOpen) {
      setCopied(false);
    }
  }, [isOpen]);

  const handleCopyLink = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = inviteLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShareLink = () => {
    if (!inviteLink) return;
    // Implementation for native sharing API if available
    if (navigator.share) {
      navigator.share({
        title: 'Connect with me on Guides Collective',
        text: 'Book a session with me through Guides Collective',
        url: inviteLink,
      });
    } else {
      // Fallback - copy link
      handleCopyLink();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-[#2C2731]/70 backdrop-blur-sm z-40"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-[#FFFCEF] border border-[#2C2731]/20 rounded-3xl shadow-[0_24px_60px_-32px_rgba(20,10,30,0.7)] max-w-md w-full mx-4 text-[#2C2731]">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-[#2C2731]/15">
            <h2 className="text-xl font-semibold text-[#2C2731]">Share Your Invite Link</h2>
            <button
              onClick={onClose}
              className="p-1 text-[#2C2731]/60 hover:text-[#2C2731] transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-6">
            <p className="text-sm text-[#2C2731]/75 mb-4">
              Share this link with clients or prospects. When they click it, 
              they'll be connected with you directly in Guides Collective.
            </p>

            {error && (
              <div className="mb-4 rounded-2xl border border-[#B9383A]/20 bg-[#B9383A]/10 p-3 text-sm text-[#B9383A]">
                {error}
                {onRetry && (
                  <button
                    onClick={onRetry}
                    className="ml-2 inline-flex items-center text-xs font-semibold text-[#B9383A] underline"
                  >
                    Try again
                  </button>
                )}
              </div>
            )}

            {/* Link Display */}
            <div className="flex items-center space-x-2 mb-4">
              <div className="flex-1 px-3 py-2 bg-white/80 border border-[#2C2731]/15 rounded-2xl">
                <input
                  type="text"
                  value={inviteLink || (loading ? 'Generating link…' : '')}
                  readOnly
                  className="w-full bg-transparent text-sm text-[#2C2731] focus:outline-none"
                />
              </div>
              <button
                onClick={handleCopyLink}
                disabled={!inviteLink || loading}
                className={`px-4 py-2 text-sm font-semibold rounded-2xl transition-colors flex items-center space-x-2 ${
                  !inviteLink || loading
                    ? 'bg-[#2C2731]/30 text-[#FFFCEF]/70 cursor-not-allowed'
                    : 'bg-[#2C2731] text-[#FFFCEF] hover:bg-[#241F2A]'
                }`}
              >
                {copied ? (
                  <>
                    <CheckIcon className="w-4 h-4" />
                    <span>Copied</span>
                  </>
                ) : (
                  <>
                    <DocumentDuplicateIcon className="w-4 h-4" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>

            {loading && (
              <p className="mb-4 text-xs text-[#2C2731]/60">Generating your invitation link…</p>
            )}

            {/* How it works */}
            <div className="bg-white/80 border border-[#2C2731]/10 rounded-2xl p-4 mb-4">
              <div className="flex items-start space-x-2">
                <InformationCircleIcon className="w-5 h-5 text-[#2C2731] mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-[#2C2731] mb-1">How it works</h3>
                  <p className="text-sm text-[#2C2731]/75">
                    When someone clicks your link, they'll see your 
                    profile and can book a session with you instantly. You'll 
                    receive a notification when they connect.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={handleShareLink}
                disabled={!inviteLink || loading}
                className={`flex-1 px-4 py-2 text-sm font-semibold rounded-2xl transition-colors flex items-center justify-center space-x-2 ${
                  !inviteLink || loading
                    ? 'bg-[#2C2731]/30 text-[#FFFCEF]/70 cursor-not-allowed'
                    : 'bg-[#2C2731] text-[#FFFCEF] hover:bg-[#241F2A]'
                }`}
              >
                <ShareIcon className="w-4 h-4" />
                <span>Share Link</span>
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 border border-[#2C2731]/25 text-[#2C2731] text-sm font-semibold rounded-2xl hover:bg-[#FFFCEF] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShareInviteLinkModal;
